import { useContext } from 'react'
import { LootContext, PersonalLootContext } from '../context'
import { levelColors, useFindRelevantWish, wishEmpty } from '../helpers'
import { usePageInfo } from 'app/frontend/base/config/queries'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { MessageCircleMore } from 'lucide-react'
import { capitalize } from 'lodash'
import { Input } from '@/components/ui/input'
import { imageUrl } from 'app/frontend/base/helpers'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import { difficulties } from 'app/frontend/types/custom/Difficulty'
import { EditableWish } from '../types'
import WishLegacyPriority from 'app/frontend/types/custom/WishLegacyPriority'
import Card from 'app/frontend/base/components/Card'

type Props = {
  item: wowaudit.WishlistItem
  currentSpecId: number
  view: keyof wowaudit.CharacterWishlist['order']
  setEditingComment: (comment: [wowaudit.WishlistItem, EditableWish]) => void
}

const PersonalWish = ({
  item,
  currentSpecId,
  view,
  setEditingComment,
}: Props) => {
  const {
    data: { gameVersion },
  } = usePageInfo()
  const { difficulty, configuration, valueDisplay } = useContext(LootContext)
  const { editing, changes, setChanges } = useContext(PersonalLootContext)
  const findRelevantWish = useFindRelevantWish(currentSpecId)

  const baseBonusIds =
    item.bonus_ids_by_configuration[
      `${configuration.id}|${difficulties.indexOf(difficulty)}`
    ]
  const itemLevel =
    item.item_level_by_configuration[
      `${configuration.id}|${difficulties.indexOf(difficulty)}`
    ]
  const wish = findRelevantWish(item)

  const isActive = wish && !wishEmpty(wish)

  const currentSettingForWish = (
    item: wowaudit.WishlistItem,
    wish?: EditableWish,
  ) => {
    const wishId = `${item.id}-${difficulty}-${currentSpecId}-${configuration.id}`
    return changes[wishId]?.type ?? wish?.type
  }

  const setEdit = (
    item: wowaudit.WishlistItem,
    wish: EditableWish,
    change: Partial<EditableWish>,
  ) => {
    const wishId = `${item.id}-${difficulty}-${currentSpecId}-${configuration.id}`
    setChanges(
      Object.assign({}, changes, {
        [wishId]: Object.assign({}, wish || { percentage: 0 }, change),
      }),
    )
  }

  const undoEdit = (item: wowaudit.WishlistItem, wish: EditableWish) => {
    setEdit(item, wish, {
      upgrade_level: 'none',
      type: 'none',
      custom_percentage: null,
    })
  }

  return (
    <Card
      className={`h-[42px] transition-[height] overflow-hidden p-0 dark:bg-accent dark:hover:bg-accent ${
        isActive
          ? `border-success-foreground ${
              wish.type !== 'raidbots' && wish.type !== 'qe'
                ? 'border-warning-foreground'
                : ''
            }${
              wish.outdated && wish.type === 'raidbots'
                ? 'border-destructive-foreground'
                : ''
            }`
          : ''
      } ${editing ? 'h-[80px]' : ''}`}
      key={item.id}
    >
      <div
        className={`flex justify-between gap-2 relative overflow-hidden p-[5px] ${
          isActive ? '' : 'grayscale'
        }`}
      >
        <a
          target='_blank'
          rel='noreferrer'
          href={`https://wowhead.com/${
            gameVersion === 'classic_progression' ? 'mop-classic/' : ''
          }item=${item.id}${itemLevel ? `?ilvl=${itemLevel}` : ''}&bonus=${
            wish ? wish.bonus_ids : baseBonusIds?.join(':')
          }`}
        >
          <BlizzardIcon
            className='border-none w-[30px] h-[30px] min-w-[30px] min-h-[30px]'
            size='large'
            iconName={item.icon}
          />
        </a>

        <div
          className={`grow leading-none overflow-hidden ${
            isActive ? '' : 'opacity-50'
          }`}
        >
          <div
            className={`truncate ${
              isActive
                ? `text-quality-${item.quality}`
                : 'text-muted-foreground'
            } ${wish?.catalyst ? 'relative pl-[22px]' : ''}`}
          >
            {wish?.catalyst ? (
              <Tooltip>
                <TooltipTrigger className='absolute left-0'>
                  <BlizzardIcon
                    size='small'
                    className='rounded-none min-w-[12px] min-h-[12px]'
                    iconName='inv_progenitor_protoformsynthesis'
                  />
                </TooltipTrigger>
                <TooltipContent>Catalyst</TooltipContent>
              </Tooltip>
            ) : (
              ''
            )}
            {item.name}
          </div>
          <div className='flex items-center mt-0.5 text-[13px]'>
            {itemLevel && <strong>{itemLevel}</strong>}

            <div
              className={'inline-block ml-1.5 opacity-60 truncate capitalize'}
            >
              {view === 'display_slot'
                ? item.encounter_name
                : item.display_slot?.replace('_', ' ')}
            </div>
          </div>
        </div>

        <div className='mx-1 flex items-center'>
          {(wish?.comment || editing) && wish?.type && wish?.type !== 'none' && (
            <Tooltip>
              <TooltipTrigger
                onClick={() => {
                  if (editing) setEditingComment([item, wish])
                }}
              >
                <MessageCircleMore
                  size={14}
                  className={`${
                    wish?.comment ? '' : 'text-muted-foreground/50'
                  }`}
                />
              </TooltipTrigger>
              <TooltipContent>
                {wish?.comment ?? (
                  <span className='italic text-muted-foreground'>
                    Click to add comment
                  </span>
                )}
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        <div className='text-success-foreground text-lg mr-0.5 flex items-center justify-center'>
          {wish?.upgrade_level && wish.upgrade_level !== 'none' ? (
            <Tooltip>
              <TooltipTrigger>
                <div
                  className={`${
                    levelColors[wish.upgrade_level]
                  } w-3 h-3 rounded-full`}
                />
              </TooltipTrigger>
              <TooltipContent>{capitalize(wish.upgrade_level)}</TooltipContent>
            </Tooltip>
          ) : !wishEmpty(wish) ? (
            <div>
              {editing && wish?.type === 'percentage' ? (
                <span className='flex items-center'>
                  <Input
                    type='text'
                    inputMode='numeric'
                    value={wish?.custom_percentage ?? ''}
                    maxLength={4}
                    className='mr-1 w-14 px-2 h-7 text-right focus-visible:ring-offset-0 focus-visible:ring-muted-foreground text-foreground'
                    onChange={(e) => {
                      setEdit(item, wish, {
                        custom_percentage: e.target.value,
                      })
                    }}
                  />
                  %
                </span>
              ) : (
                <span>
                  {valueDisplay === 'percentage' || wish?.custom_percentage
                    ? `${
                        wish?.custom_percentage ??
                        wish?.percentage?.toLocaleString('en-US', {
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                        }) ??
                        0
                      }%`
                    : wish?.absolute?.toLocaleString('en-US', {
                        maximumFractionDigits: 0,
                      })}
                </span>
              )}
            </div>
          ) : null}
        </div>
        {wish &&
          !wishEmpty(wish) &&
          wish.type !== 'raidbots' &&
          wish.type !== 'qe' && (
            <Tooltip>
              <TooltipTrigger>
                <div className='absolute -right-5 -top-5 w-8 h-8 cursor-pointer rotate-45 bg-warning-foreground' />
              </TooltipTrigger>
              <TooltipContent>Manually edited</TooltipContent>
            </Tooltip>
          )}
        {!wishEmpty(wish) && wish?.outdated && wish.type === 'raidbots' && (
          <Tooltip>
            <TooltipTrigger>
              <div className='issue-indicator bg-destructive-foreground' />
            </TooltipTrigger>
            <TooltipContent>Outdated</TooltipContent>
          </Tooltip>
        )}
      </div>

      <div
        className={`${
          editing ? 'opacity-100' : 'opacity-0 pointer-events-none'
        } transition-opacity `}
      >
        <ul className='flex my-1 mx-2 border border-accent rounded-md'>
          {['tiny', 'small', 'big', 'huge'].map((level, i) => (
            <li
              key={level}
              className={`py-1 px-2 flex items-center justify-center border-r gap-1 capitalize text-xs cursor-pointer grow transition-all whitespace-nowrap truncate ${
                i === 0 ? 'rounded-l-md' : ''
              } ${
                currentSettingForWish(item, wish) === level
                  ? 'bg-success-foreground'
                  : ''
              }`}
              onClick={() => {
                if (wish?.type === level) {
                  undoEdit(item, wish)
                } else {
                  setEdit(item, wish, {
                    upgrade_level: level as WishLegacyPriority,
                    type: level as WishLegacyPriority,
                    custom_percentage: null,
                  })
                }
              }}
            >
              <span
                className={`${
                  levelColors[level as WishLegacyPriority]
                } w-2.5 h-2.5 border border-accent min-w-2.5 min-h-2.5 rounded-full`}
              />
              <span className='truncate'>{level}</span>
            </li>
          ))}
          <li
            className={`py-1 px-2 flex items-center justify-center gap-1 capitalize text-xs cursor-pointer grow transition-all whitespace-nowrap ${
              currentSettingForWish(item, wish) === 'percentage'
                ? 'bg-success-foreground'
                : ''
            } ${wish?.droptimizer_report ? 'border-r' : 'rounded-r-md'}`}
            onClick={() => {
              if (wish?.type === 'percentage') {
                undoEdit(item, wish)
              } else {
                setEdit(item, wish, {
                  upgrade_level: null,
                  type: 'percentage',
                  custom_percentage: wish?.percentage || '',
                })
              }
            }}
          >
            %
          </li>
          {wish?.droptimizer_report && (
            <li
              className={`px-2 flex items-center justify-center grow rounded-r-md ${
                ['raidbots', 'qe'].includes(
                  currentSettingForWish(item, wish) as string,
                )
                  ? 'bg-success-foreground'
                  : ''
              }`}
              onClick={() => {
                if (wish?.type === 'raidbots' || wish?.type === 'qe') {
                  undoEdit(item, wish)
                } else {
                  setEdit(item, wish, {
                    upgrade_level: null,
                    type: wish?.original_type === 'qe' ? 'qe' : 'raidbots',
                    custom_percentage: null,
                  })
                }
              }}
            >
              {wish?.original_type === 'qe' ? (
                <img
                  className='w-5 min-w-5'
                  title='Questionably Epic'
                  src={imageUrl('qe-logo.png')}
                ></img>
              ) : (
                <img
                  className='w-4 min-w-4'
                  title='Raidbots'
                  src={imageUrl('raidbots-logo-head.svg')}
                ></img>
              )}
            </li>
          )}
        </ul>
      </div>
    </Card>
  )
}

export default PersonalWish
